import React, { useState } from 'react';
import {
  Box, Typography, Grid, TextField,
  Button, FormControlLabel, Switch,
  Accordion, AccordionSummary, AccordionDetails, Alert, Snackbar, Stack
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon
} from '@mui/icons-material';

const AdminSettings = () => {
  // State for settings
  const [settings, setSettings] = useState({
    // General settings
    appName: 'Magellan Languages',
    supportEmail: 'support@magellanlanguages.com',

    // Content generation settings
    openaiApiKey: '************************************************',
    defaultModel: 'gpt-4o-mini',
    maxTokens: 2000,

    // Audio settings
    enableAudioGeneration: true,
    audioProviderApiKey: '************************************************',

    // User settings
    allowRegistration: true,
    requireInviteCode: true,
    defaultUserRole: 'user',

    // Analytics settings
    enableAnalytics: false,
    analyticsProvider: '',
    analyticsApiKey: ''
  });

  // Set any changes
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Notification state
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
  };

  // Handle switch change
  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: checked
    }));
    setHasChanges(true);
  };

  // Handle save settings
  const handleSaveSettings = () => {
    setSaving(true);

    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setHasChanges(false);

      // Show success notification
      setNotification({
        open: true,
        message: 'Settings saved successfully',
        severity: 'success'
      });
    }, 1500);
  };

  // Close notification
  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  const accordionSx = {
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    '&:before': { display: 'none' },
    boxShadow: 'none',
    overflow: 'hidden',
  };

  return (
    <Box>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3, gap: 1 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Admin Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Configure application, content, audio, user, and analytics options.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSaveSettings}
          disabled={!hasChanges || saving}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Stack>

      <Grid container spacing={3}>
        {/* General Settings */}
        <Grid item xs={12}>
          <Accordion defaultExpanded disableGutters sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="general-settings-content"
              id="general-settings-header"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>General Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Application Name"
                    name="appName"
                    value={settings.appName}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Support Email"
                    name="supportEmail"
                    value={settings.supportEmail}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Content Generation Settings */}
        <Grid item xs={12}>
          <Accordion disableGutters sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="content-settings-content"
              id="content-settings-header"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Content Generation Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="OpenAI API Key"
                    name="openaiApiKey"
                    value={settings.openaiApiKey}
                    onChange={handleInputChange}
                    variant="outlined"
                    type="password"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Default Model"
                    name="defaultModel"
                    value={settings.defaultModel}
                    onChange={handleInputChange}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Max Tokens"
                    name="maxTokens"
                    value={settings.maxTokens}
                    onChange={handleInputChange}
                    variant="outlined"
                    type="number"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Audio Settings */}
        <Grid item xs={12}>
          <Accordion disableGutters sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="audio-settings-content"
              id="audio-settings-header"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Audio Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enableAudioGeneration}
                        onChange={handleSwitchChange}
                        name="enableAudioGeneration"
                      />
                    }
                    label="Enable Audio Generation"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Audio Provider API Key"
                    name="audioProviderApiKey"
                    value={settings.audioProviderApiKey}
                    onChange={handleInputChange}
                    variant="outlined"
                    type="password"
                    disabled={!settings.enableAudioGeneration}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* User Settings */}
        <Grid item xs={12}>
          <Accordion disableGutters sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="user-settings-content"
              id="user-settings-header"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>User Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.allowRegistration}
                        onChange={handleSwitchChange}
                        name="allowRegistration"
                      />
                    }
                    label="Allow New User Registration"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.requireInviteCode}
                        onChange={handleSwitchChange}
                        name="requireInviteCode"
                        disabled={!settings.allowRegistration}
                      />
                    }
                    label="Require Invite Code"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Default User Role"
                    name="defaultUserRole"
                    value={settings.defaultUserRole}
                    onChange={handleInputChange}
                    variant="outlined"
                    select
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="user">Regular User</option>
                    <option value="editor">Editor</option>
                    <option value="content_creator">Content Creator</option>
                  </TextField>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Analytics Settings */}
        <Grid item xs={12}>
          <Accordion disableGutters sx={accordionSx}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="analytics-settings-content"
              id="analytics-settings-header"
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Analytics Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.enableAnalytics}
                        onChange={handleSwitchChange}
                        name="enableAnalytics"
                      />
                    }
                    label="Enable Analytics"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Analytics Provider"
                    name="analyticsProvider"
                    value={settings.analyticsProvider}
                    onChange={handleInputChange}
                    variant="outlined"
                    disabled={!settings.enableAnalytics}
                    select
                    SelectProps={{
                      native: true,
                    }}
                  >
                    <option value="">Select Provider</option>
                    <option value="google">Google Analytics</option>
                    <option value="mixpanel">Mixpanel</option>
                    <option value="segment">Segment</option>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Analytics API Key"
                    name="analyticsApiKey"
                    value={settings.analyticsApiKey}
                    onChange={handleInputChange}
                    variant="outlined"
                    disabled={!settings.enableAnalytics || !settings.analyticsProvider}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>

      {/* Success notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminSettings;